import React from "react";
import { enterpriseViewModel } from "common/viewModels";
import SummaryRow from "./SummaryRow";
import * as utils from "common/utils";
import Grid from "components/Grid";
import GridCell from "components/GridCell";
import Alert from "components/Alert";
import FileSummary from "./FileSummary";
import Watermark from "components/Watermark";
import { Doughnut } from "react-chartjs-2";

const modSourceLabel = (s, refData) => {
    const label = utils.valueToText(refData.animalFeedSources, s);
    const modLabel = s === "FodderSupplements" ? "Imported fodder supplements" : s === "OtherSupplements" ? "Imported other supplements" : label;
    return modLabel;
};

const getFeedTotals = (after, before, refData, farm) => {
    const getRsuSourceTotals = (budget, ent) => {
        const sourceTotals = { enterpriseTotals: [], sourceNames: [], sources: [], total: 0 };
        const { currentResults = {} } = budget;
        const { animalResults = {} } = currentResults;

        ent.forEach((e, i) => {
            const { sources = {} } = animalResults[e.id] || {};
            Object.keys(sources).forEach((st, i) => {
                const amount = sources[st].produced.reduce((t, m) => (t += m.perAnimal), 0);
                const proportion = utils.round((sources[st].produced.reduce((t, m) => (t += m.proportion), 0) * 100) / 12, 0);
                if (amount > 0) {
                    if (!sourceTotals.sources[st]) {
                        sourceTotals.sources[st] = {
                            total: 0,
                            proportion: 0,
                            enterprises: [],
                            label: st,
                        };
                    }
                    sourceTotals.sources[st].total += amount;
                    sourceTotals.sources[st].proportion += proportion;
                    let enterprise = sourceTotals.sources[st].enterprises.find((ent) => ent.id === e.id);
                    if (!enterprise) {
                        enterprise = {
                            id: e.id,
                            name: e.enterpriseType,
                            type: e.enterpriseTypeEnum,
                            total: 0,
                            proportion: 0,
                        };
                        sourceTotals.sources[st].enterprises.push(enterprise);
                    }
                    let enterpriseTotals = sourceTotals.enterpriseTotals.find((e) => e.type === enterprise.type);
                    if (!enterpriseTotals) {
                        enterpriseTotals = { type: enterprise.type, total: 0 };
                        sourceTotals.enterpriseTotals.push(enterpriseTotals);
                    }
                    if (!sourceTotals.sourceNames.includes(st)) {
                        sourceTotals.sourceNames.push(st);
                    }
                    enterprise.total += amount;
                    sourceTotals.total += amount;
                    enterpriseTotals.total += amount;

                    enterprise.proportion += proportion;
                    sourceTotals.proportion += proportion;
                    enterpriseTotals.proportion += proportion;
                }
            });
        });

        return sourceTotals;
    };

    const actualEnterprises = enterpriseViewModel(farm, after.details, refData);
    const actualRsuSourceTotals = getRsuSourceTotals(after.details, actualEnterprises);
    const beforeEnterprises = before && enterpriseViewModel(farm, before.details, refData);
    const baseRsuSourceTotals = before && getRsuSourceTotals(before.details, beforeEnterprises);

    const enterpriseTypes = actualRsuSourceTotals.enterpriseTotals.map((e) => e.type);
    if (baseRsuSourceTotals) {
        baseRsuSourceTotals.enterpriseTotals.forEach((e) => {
            if (!enterpriseTypes.includes(e.type)) {
                enterpriseTypes.push(e.type);
            }
        });
    }
    const sourceNames = actualRsuSourceTotals.sourceNames.map((e) => e);
    if (baseRsuSourceTotals) {
        baseRsuSourceTotals.sourceNames.forEach((e) => {
            if (!sourceNames.includes(e)) {
                sourceNames.push(e);
            }
        });
    }

    return { actualRsuSourceTotals, baseRsuSourceTotals, sourceNames, enterpriseTypes };
};

export const FeedSummary = ({ before, after, refData, farm, feedTotals, heading }) => {
    const { baseRsuSourceTotals, actualRsuSourceTotals, sourceNames } = feedTotals || getFeedTotals(after, before, refData, farm);
    if (actualRsuSourceTotals.total === 0 && (!baseRsuSourceTotals || baseRsuSourceTotals.total === 0)) return null;

    return (
        <div className="ReportSection">
            <h1>Feed</h1>
            <div className="Table Table-Compressed">
                <Watermark />
                <table>
                    <thead>
                        <tr>
                            <th data-width="25"></th>
                            <th></th>
                            {before && (
                                <th className="u-textCenter u-before" data-width="12.5">
                                    {utils.truncateText(before.displayName, 35)}
                                </th>
                            )}
                            <th className={before ? "u-after u-textCenter" : "u-textCenter"} data-width="12.5">
                                {utils.truncateText(after.displayName, 35)}
                            </th>
                            {before && (
                                <th className="u-textCenter" data-width="16.7">
                                    Change
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        <SummaryRow category="RSU" baseValue={before ? (baseRsuSourceTotals ? baseRsuSourceTotals.total : 0) : undefined} actualValue={actualRsuSourceTotals.total} label="Total" unit="RSU" />
                        {sourceNames.map((s, i) => {
                            return <SummaryRow key={i} category=" " prefix="RSU" baseValue={before ? (baseRsuSourceTotals && baseRsuSourceTotals.sources[s] ? baseRsuSourceTotals.sources[s].total : 0) : undefined} actualValue={actualRsuSourceTotals.sources[s] && actualRsuSourceTotals.sources[s].total} label={modSourceLabel(s, refData)} unit="RSU" />;
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const FeedExtraSummary = ({ before, after, refData, farm, feedTotals }) => {
    const { baseRsuSourceTotals, actualRsuSourceTotals, sourceNames, enterpriseTypes } = feedTotals || getFeedTotals(after, before, refData, farm);

    const options = {
        plugins: {
            legend: {
                display: true,
            },
        },
        responsive: false,
        maintainAspectRatio: false,
    };

    return enterpriseTypes.map((e, i) => {
        const actualEnterprise = actualRsuSourceTotals && actualRsuSourceTotals.enterpriseTotals.find((t) => t.type === e);
        const baseEnterprise = baseRsuSourceTotals && baseRsuSourceTotals.enterpriseTotals.find((t) => t.type === e);

        const actualEnterprisePieData =
            actualEnterprise &&
            sourceNames.map((st) => {
                const actEntSource = actualRsuSourceTotals && actualRsuSourceTotals.sources[st] && actualRsuSourceTotals.sources[st].enterprises.find((ent) => ent.type === e);
                return actEntSource ? actEntSource.proportion : 0;
            });

        const baseEnterprisePieData =
            baseEnterprise &&
            sourceNames.map((st) => {
                const baseEntSource = baseRsuSourceTotals && baseRsuSourceTotals.sources[st] && baseRsuSourceTotals.sources[st].enterprises.find((ent) => ent.type === e);
                return baseEntSource ? baseEntSource.proportion : 0;
            });

        const beforeData = before && {
            labels: sourceNames.map((s) => modSourceLabel(s, refData)),
            datasets: [
                {
                    data: baseEnterprisePieData,
                    backgroundColor: sourceNames.map((g, i) => utils.graphColours[i + 1]),
                    hoverBackgroundColor: sourceNames.map((g, i) => utils.graphColours[i + 1]),
                },
            ],
        };

        const afterData = {
            labels: sourceNames.map((s) => modSourceLabel(s, refData)),
            datasets: [
                {
                    data: actualEnterprisePieData,
                    backgroundColor: sourceNames.map((g, i) => utils.graphColours[i + 1]),
                    hoverBackgroundColor: sourceNames.map((g, i) => utils.graphColours[i + 1]),
                },
            ],
        };

        return (
            <div key={i} className="ReportSection">
                <h1>RSU - {utils.valueToText(refData.enterpriseTypes, e)}</h1>
                <div className="Table Table-Compressed">
                    <Watermark />
                    <table>
                        <thead>
                            <tr>
                                <th></th>
                                {before && (
                                    <th className="u-textCenter u-before" data-width="12.5">
                                        {utils.truncateText(before.displayName, 35)}
                                    </th>
                                )}
                                <th className={before ? "u-after u-textCenter" : "u-textCenter"} data-width="12.5">
                                    {utils.truncateText(after.displayName, 35)}
                                </th>
                                {before && (
                                    <th className="u-textCenter" data-width="16.7">
                                        Change
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            <SummaryRow baseValue={before ? (baseEnterprise ? baseEnterprise.total : 0) : undefined} actualValue={actualEnterprise ? actualEnterprise.total : 0} label="Total" unit="RSU" />
                            {sourceNames.map((st) => {
                                const actualEntSource = actualRsuSourceTotals && actualRsuSourceTotals.sources[st] && actualRsuSourceTotals.sources[st].enterprises.find((ent) => ent.type === e);
                                const baseEntSource = baseRsuSourceTotals && baseRsuSourceTotals.sources[st] && baseRsuSourceTotals.sources[st].enterprises.find((ent) => ent.type === e);
                                if (!actualEntSource && !baseEntSource) return null;
                                return <SummaryRow key={i + st} baseValue={before ? (baseEntSource ? baseEntSource.total : 0) : undefined} actualValue={actualEntSource ? actualEntSource.total : 0} label={modSourceLabel(st, refData)} unit="RSU" />;
                            })}
                        </tbody>
                    </table>
                </div>
                {false && (
                    <div className="u-mb-lg u-print-none">
                        <Grid>
                            {before && (
                                <GridCell className="u-width1of2">
                                    {beforeData && beforeData.datasets[0] && beforeData.datasets[0].data && beforeData.datasets[0].data.length > 0 && (
                                        <>
                                            <div className="h4 u-textCenter">{before.displayName}</div>
                                            <div className="u-flex u-flexJustifyCenter">
                                                <Doughnut data={beforeData} options={options} height={400} width={400} />
                                            </div>
                                        </>
                                    )}
                                </GridCell>
                            )}
                            <GridCell className="u-width1of2">
                                {afterData && afterData.datasets[0] && afterData.datasets[0].data && afterData.datasets[0].data.length > 0 && (
                                    <>
                                        <div className="h4 u-textCenter">{after.displayName}</div>
                                        <div className="u-flex u-flexJustifyCenter">
                                            <Doughnut data={afterData} options={options} height={400} width={400} />
                                        </div>
                                    </>
                                )}
                            </GridCell>
                        </Grid>
                    </div>
                )}
            </div>
        );
    });
};

const Feed = (props) => {
    const { after, before, refData, farm, showFileComparison = true } = props;
    const feedTotals = getFeedTotals(after, before, refData, farm);

    if (!feedTotals || (feedTotals.actualRsuSourceTotals.total === 0 && (!feedTotals.baseRsuSourceTotals || feedTotals.baseRsuSourceTotals.total === 0))) {
        return (
            <div className="u-pt-lg">
                <Alert text="The selected analysis does not contain feed information" type="Info" />
            </div>
        );
    }

    return (
        <div className="ReportPage">
            {showFileComparison && before && <FileSummary before={before} after={after} mini={true} />}
            <FeedSummary {...props} feedTotals={feedTotals} />
            <FeedExtraSummary {...props} feedTotals={feedTotals} />
        </div>
    );
};

export default Feed;
